import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { fileUrl, API } from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Download, FileText, ChevronLeft } from "lucide-react";

// Match PDF box score columns exactly:
// HEADERS = ["#", "Player", "PTS", "1PT", "2PT", "FG%", "AST", "RO", "RD", "RT", "TO", "STL", "PF", "EF"]
const STAT_COLS = [
  { key: "pts", label: "PTS" },
  { key: "pt1", label: "1PT", fmt: (s) => `${s.pts1m || 0}/${s.pts1a || 0}` },
  { key: "pt2", label: "2PT", fmt: (s) => `${s.pts2m || 0}/${s.pts2a || 0}` },
  { key: "fg_pct", label: "FG%", fmt: (s) => `${s.fg_pct || 0}%` },
  { key: "ast", label: "AST" },
  { key: "oreb", label: "RO" },
  { key: "dreb", label: "RD" },
  { key: "reb", label: "RT" },
  { key: "tov", label: "TO" },
  { key: "stl", label: "STL" },
  { key: "pf", label: "PF" },
  { key: "eff", label: "EF" },
];

const LEGEND_ITEMS = [
  ["PTS", "Pontos Totais"], ["RO", "Rebote Ofensivo"],
  ["1PT", "1 Ponto (convertidos/tentados)"], ["RD", "Rebote Defensivo"],
  ["2PT", "2 Pontos (convertidos/tentados)"], ["RT", "Rebotes Totais"],
  ["FG%", "Porcentagem de Acertos"], ["TO", "Turnovers"],
  ["AST", "Assistências"], ["STL", "Roubadas de Bola"],
  ["EF", "Eficiência"], ["PF", "Faltas Pessoais"],
  ["*", "Convocado para o jogo"], ["", ""],
];

export default function PublicGameDetail() {
  const { t } = useTranslation();
  const { id } = useParams();
  const [box, setBox] = useState(null);
  const [actions, setActions] = useState([]);
  const [players, setPlayers] = useState({});

  useEffect(() => {
    Promise.all([api.get(`/games/${id}/boxscore`), api.get(`/games/${id}/actions`)]).then(([b, a]) => {
      setBox(b.data);
      setActions(a.data);
      const all = [...b.data.players_a, ...b.data.players_b];
      setPlayers(Object.fromEntries(all.map((p) => [p.id, p])));
    });
  }, [id]);

  if (!box) return <div className="max-w-7xl mx-auto p-12 text-ink-muted">Loading…</div>;
  const { game, team_a, team_b, players_a, players_b, team_stats_a, team_stats_b } = box;

  return (
    <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-8 py-6 md:py-8">
      <Link to="/games" className="inline-flex items-center gap-1 text-ink-muted hover:text-orange text-xs uppercase tracking-widest font-heading mb-4" data-testid="back-to-games">
        <ChevronLeft size={14} /> {t("game.back_to_games")}
      </Link>

      {/* Scoreboard */}
      <div className="border border-navy-border rounded-sm overflow-hidden bg-navy-deep">
        <div className="px-3 md:px-4 py-2 bg-navy-surface border-b border-navy-border flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-3 min-w-0">
            <span className={`px-2 py-1 text-[10px] font-heading uppercase tracking-widest rounded-sm ${game.status === "live" ? "bg-court-red text-white animate-pulse" : game.status === "finished" ? "bg-navy-deep text-ink-secondary border border-navy-border" : "bg-orange text-white"}`}>{game.status}</span>
            <span className="font-mono text-xs text-ink-muted truncate">{game.date} · {game.time || ""}</span>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <a href={`${API}/games/${id}/boxscore.pdf`} target="_blank" rel="noreferrer" data-testid="download-boxscore-pdf">
              <Button variant="outline" className="bg-transparent border-navy-border text-white hover:bg-navy-surface hover:text-orange font-heading uppercase tracking-widest text-[10px] md:text-xs rounded-sm h-9 px-2 md:px-3">
                <Download size={12} className="mr-1" /> Box Score
              </Button>
            </a>
            <a href={`${API}/games/${id}/playbyplay.pdf`} target="_blank" rel="noreferrer" data-testid="download-pbp-pdf">
              <Button variant="outline" className="bg-transparent border-navy-border text-white hover:bg-navy-surface hover:text-orange font-heading uppercase tracking-widest text-[10px] md:text-xs rounded-sm h-9 px-2 md:px-3">
                <FileText size={12} className="mr-1" /> PBP
              </Button>
            </a>
            <a href={`/scoreboard/${id}`} target="_blank" rel="noreferrer" data-testid="open-public-scoreboard">
              <Button variant="outline" className="bg-transparent border-orange/40 text-orange hover:bg-orange hover:text-white font-heading uppercase tracking-widest text-[10px] md:text-xs rounded-sm h-9 px-2 md:px-3">
                Scoreboard
              </Button>
            </a>
          </div>
        </div>
        <div className="grid grid-cols-3 items-center gap-2 md:gap-4 p-4 md:p-8">
          <TeamHeader team={team_a} side="left" />
          <div className="text-center">
            <div className="font-mono tabular text-4xl sm:text-6xl md:text-7xl lg:text-8xl font-black text-white leading-none">
              <span className={game.score_a > game.score_b && game.status === "finished" ? "text-orange" : ""}>{game.score_a}</span>
              <span className="text-ink-muted mx-1 md:mx-3">–</span>
              <span className={game.score_b > game.score_a && game.status === "finished" ? "text-orange" : ""}>{game.score_b}</span>
            </div>
            <div className="mt-2 text-[10px] md:text-xs font-heading uppercase tracking-[0.3em] text-ink-muted">{game.overtime ? t("game.overtime") : t("game.regulation")}</div>
            <div className="mt-2 flex items-center justify-center gap-3 md:gap-4 text-sm md:text-base font-heading uppercase tracking-widest">
              <span className="text-ink-muted">{t("game.fouls")}</span>
              <span className={`font-mono tabular font-black text-2xl md:text-3xl ${game.fouls_a >= 6 ? "text-court-red" : "text-white"}`} data-testid="pub-fouls-a">{game.fouls_a}</span>
              <span className="text-ink-muted">—</span>
              <span className={`font-mono tabular font-black text-2xl md:text-3xl ${game.fouls_b >= 6 ? "text-court-red" : "text-white"}`} data-testid="pub-fouls-b">{game.fouls_b}</span>
            </div>
            {(game.fouls_a >= 6 || game.fouls_b >= 6) && (
              <div className="mt-1 text-xs md:text-sm font-heading uppercase tracking-widest text-court-red font-bold" data-testid="pub-bonus-badge">
                ⚠ {t("game.bonus_active").replace("⚠ ", "")}
              </div>
            )}
          </div>
          <TeamHeader team={team_b} side="right" />
        </div>
      </div>

      <Tabs defaultValue="box" className="mt-6 md:mt-8">
        <TabsList className="bg-navy-deep border border-navy-border rounded-sm p-1">
          <TabsTrigger value="box" data-testid="tab-boxscore" className="font-heading uppercase tracking-widest text-xs data-[state=active]:bg-orange data-[state=active]:text-white rounded-sm">{t("game.box_score")}</TabsTrigger>
          <TabsTrigger value="pbp" data-testid="tab-pbp" className="font-heading uppercase tracking-widest text-xs data-[state=active]:bg-orange data-[state=active]:text-white rounded-sm">{t("game.play_by_play")}</TabsTrigger>
        </TabsList>
        <TabsContent value="box" className="mt-4 space-y-6 md:space-y-8">
          <BoxTable team={team_a} players={players_a} totals={team_stats_a} />
          <BoxTable team={team_b} players={players_b} totals={team_stats_b} />
        </TabsContent>
        <TabsContent value="pbp" className="mt-4">
          <PlayByPlay actions={actions} players={players} game={game} />
        </TabsContent>
      </Tabs>

      {/* Legend footer (matches PDF LEGENDA) */}
      <div className="mt-8 md:mt-10 border border-navy-border rounded-sm bg-navy-deep p-4 md:p-5" data-testid="game-legend">
        <div className="font-heading uppercase tracking-widest text-orange text-xs md:text-sm mb-3">{t("game.legend")}</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-xs md:text-sm">
          {LEGEND_ITEMS.map(([k, v], i) => (
            k ? (
              <div key={i} className="flex gap-2">
                <span className="font-heading font-bold text-orange w-12 shrink-0">{k}</span>
                <span className="text-ink-secondary">{v}</span>
              </div>
            ) : <div key={i} className="hidden sm:block" />
          ))}
        </div>
      </div>
    </div>
  );
}

function TeamHeader({ team, side }) {
  return (
    <div className={`flex items-center gap-2 md:gap-4 ${side === "right" ? "justify-end flex-row-reverse md:flex-row" : ""}`}>
      <div className="w-12 h-12 md:w-20 md:h-20 rounded-sm bg-navy-surface border border-navy-border grid place-items-center overflow-hidden shrink-0">
        {team?.logo_url ? <img src={fileUrl(team.logo_url)} alt="" className="w-full h-full object-cover" />
          : <span className="font-heading font-black text-xl md:text-3xl text-orange">{team?.name?.[0] || "?"}</span>}
      </div>
      <div className={`${side === "right" ? "md:text-right" : ""} min-w-0`}>
        <div className="font-heading font-black uppercase tracking-tight text-sm sm:text-lg md:text-2xl lg:text-3xl text-white truncate">{team?.name || "TBD"}</div>
        <Link to={`/teams/${team?.id}`} className="hidden md:inline text-xs uppercase tracking-widest font-heading text-orange hover:underline">View roster →</Link>
      </div>
    </div>
  );
}

function BoxTable({ team, players, totals }) {
  return (
    <div className="border border-navy-border rounded-sm overflow-hidden bg-navy-deep">
      <div className="px-3 md:px-4 py-3 bg-navy-surface border-b border-navy-border flex items-center gap-3">
        <span className="font-heading font-black uppercase tracking-tight text-base md:text-xl text-white truncate">{team?.name}</span>
        <span className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">Box Score</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs md:text-sm min-w-[720px]">
          <thead>
            <tr className="bg-navy-surface/50 text-ink-muted text-[10px] uppercase tracking-widest">
              <th className="text-left px-2 md:px-3 py-2">#</th>
              <th className="text-left px-2 md:px-3 py-2">Player</th>
              {STAT_COLS.map((c) => <th key={c.key} className="text-center px-1.5 md:px-2 py-2">{c.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {players.map((p) => (
              <tr key={p.id} className={`border-t border-navy-border ${p.is_active ? "" : "opacity-50"}`}>
                <td className="px-2 md:px-3 py-2 font-mono tabular text-orange font-bold">{p.jersey_number}</td>
                <td className="px-2 md:px-3 py-2">
                  <Link to={`/players/${p.id}`} className="hover:text-orange font-heading uppercase tracking-tight whitespace-nowrap">
                    {p.full_name}{p.is_active && <span className="text-orange ml-1">*</span>}
                  </Link>
                </td>
                {STAT_COLS.map((c) => (
                  <td key={c.key} className={`text-center px-1.5 md:px-2 py-2 font-mono tabular ${c.key === "pts" ? "text-orange font-bold" : "text-ink-secondary"}`}>
                    {c.fmt ? c.fmt(p.stats) : (p.stats[c.key] ?? 0)}
                  </td>
                ))}
              </tr>
            ))}
            <tr className="border-t border-navy-border bg-navy-surface/60">
              <td className="px-2 md:px-3 py-2"></td>
              <td className="px-2 md:px-3 py-2 font-heading uppercase tracking-widest text-orange text-xs">Team</td>
              {STAT_COLS.map((c) => (
                <td key={c.key} className="text-center px-1.5 md:px-2 py-2 font-mono tabular text-white font-bold">
                  {c.fmt ? c.fmt(totals) : (totals[c.key] ?? 0)}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

function describe(a, players) {
  const p = players[a.player_id];
  const who = p ? `#${p.jersey_number} ${p.full_name}` : (a.team || "");
  if (a.type === "made_shot") {
    const ast = players[a.assist_player_id];
    return `${who} made ${a.points}PT shot${ast ? ` (AST #${ast.jersey_number} ${ast.full_name})` : ""}`;
  }
  if (a.type === "missed_shot") return `${who} missed ${a.points}PT shot`;
  if (a.type === "rebound") return `${who} ${a.rebound_kind} rebound`;
  if (a.type === "foul") {
    const fp = players[a.fouled_player_id];
    return `${(a.foul_type || "personal").toUpperCase()} foul by ${who}${fp ? ` on #${fp.jersey_number} ${fp.full_name}` : ""}`;
  }
  if (a.type === "turnover") return `Turnover by ${who}${a.twelve_second ? " (12-sec violation)" : ""}`;
  if (a.type === "steal") return `Steal by ${who}`;
  if (a.type === "timeout") return `Timeout · Team ${a.team || "?"}`;
  return a.type;
}

function PlayByPlay({ actions, players, game }) {
  let sa = 0, sb = 0;
  const rows = actions.map((a) => {
    if (a.type === "made_shot") {
      if (a.team === "A") sa += a.points || 0;
      else if (a.team === "B") sb += a.points || 0;
    }
    return { a, sa, sb };
  }).reverse();

  return (
    <div className="border border-navy-border rounded-sm overflow-hidden bg-navy-deep">
      {rows.length === 0 ? (
        <div className="p-12 text-center text-ink-muted">No events recorded yet.</div>
      ) : (
        <div className="divide-y divide-navy-border max-h-[600px] overflow-y-auto">
          {rows.map(({ a, sa, sb }) => (
            <div key={a.id} className="grid grid-cols-12 items-center gap-2 px-3 md:px-4 py-2.5 md:py-3">
              <div className="col-span-2 md:col-span-2 font-mono tabular text-[10px] md:text-xs text-ink-muted">{a.clock || "—"}</div>
              <div className="col-span-2 md:col-span-2">
                <span className={`px-1.5 md:px-2 py-0.5 text-[9px] md:text-[10px] font-heading uppercase tracking-widest rounded-sm ${a.team === "A" ? "bg-orange/20 text-orange" : a.team === "B" ? "bg-navy-surface text-ink-secondary" : "bg-transparent text-ink-muted"}`}>
                  {a.team === "A" ? "A" : a.team === "B" ? "B" : "—"}
                </span>
              </div>
              <div className="col-span-6 md:col-span-6 text-xs md:text-sm break-words">{describe(a, players)}</div>
              <div className="col-span-2 md:col-span-2 text-right font-mono tabular text-xs md:text-sm">{sa}–{sb}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
